import { Link } from "react-router-dom";

import { leaveApi } from "../api";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { useAsyncData } from "../hooks";
import type { LeaveEntry, OfficialDutyResponse } from "../types";

export default function LeaveOnLeavePage() {
  const { data, loading, error, reload } = useAsyncData(
    () => leaveApi.whoIsOnLeave(),
    [],
  );

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="Employees on Leave"
        actions={
          <div className="flex items-center gap-2">
            <LeaveBackButton to="/hr/leave" label="Dashboard" />
            <button
              type="button"
              onClick={reload}
              className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50"
            >
            Refresh
            </button>
          </div>
        }
      />

      {loading ? <LeaveLoading label="Loading leave activity..." /> : null}
      {error ? <LeaveError message={error} onRetry={reload} /> : null}

      {data ? (
        <>
          <LeaveSection
            title="Currently on Leave"
            items={data.currentlyOnLeave}
            tone="emerald"
            emptyText="No employees are on leave today."
          />

          <LeaveSection
            title="Pending Approval — Due Today or Overdue"
            items={data.pendingApproval}
            tone="amber"
            emptyText="No overdue approvals."
          />

          <LeaveSection
            title="Upcoming Leave (next 30 days)"
            items={data.upcomingLeave}
            tone="blue"
            emptyText="No upcoming leave scheduled."
          />

          <OfficialDutySection items={data.onOfficialDuty ?? []} />

          {data.currentlyOnLeave.length === 0 &&
            data.pendingApproval.length === 0 &&
            data.upcomingLeave.length === 0 &&
            (data.onOfficialDuty ?? []).length === 0 && (
              <div className="rounded-2xl border bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
                No active or upcoming leave or official duty found.
              </div>
            )}
        </>
      ) : null}
    </div>
  );
}

function LeaveSection({
  title,
  subtitle,
  items,
  tone,
  emptyText,
}: {
  title: string;
  subtitle?: string;
  items: LeaveEntry[];
  tone: "emerald" | "amber" | "blue";
  emptyText?: string;
}) {
  const headerColors = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    blue: "border-blue-200 bg-blue-50 text-blue-900",
  };
  const countColors = {
    emerald: "bg-emerald-200 text-emerald-800",
    amber: "bg-amber-200 text-amber-800",
    blue: "bg-blue-200 text-blue-800",
  };

  return (
    <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
      <div className={`flex flex-wrap items-center gap-3 border-b px-5 py-3 ${headerColors[tone]}`}>
        <div>
          <span className="font-black">{title}</span>
          {subtitle && <span className="ml-2 text-xs font-normal opacity-70">{subtitle}</span>}
        </div>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${countColors[tone]}`}>
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        emptyText ? (
          <p className="px-5 py-6 text-sm text-slate-500">{emptyText}</p>
        ) : null
      ) : (
        <div className="divide-y">
          {items.map((item) => (
            <LeaveEntryRow key={item.applicationId} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function LeaveEntryRow({ item }: { item: LeaveEntry }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
      <div className="flex min-w-48 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-black text-slate-600">
          {item.employeeName.charAt(0).toUpperCase()}
        </div>
        <div>
          <div className="font-black text-slate-900">{item.employeeName}</div>
          {item.departmentName && (
            <div className="text-xs text-slate-400">{item.departmentName}</div>
          )}
        </div>
      </div>

      <div className="min-w-36">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Leave Type</div>
        <div className="mt-0.5 font-semibold text-slate-700">{item.leaveTypeName}</div>
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Period</div>
        <div className="mt-0.5 font-semibold text-slate-700">
          {formatDate(item.startDate)} → {formatDate(item.endDate)}
        </div>
        <div className="text-xs text-slate-400">{item.requestedDays} day(s)</div>
      </div>

      {item.currentApproverUsername ? (
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Awaiting</div>
          <div className="mt-0.5 text-sm text-slate-600">{item.currentApproverUsername}</div>
        </div>
      ) : null}

      <Link
        to={`/hr/leave/requests/${item.applicationId}`}
        className="text-sm font-bold text-emerald-700 hover:text-emerald-900"
      >
        View →
      </Link>
    </div>
  );
}

function OfficialDutySection({ items }: { items: OfficialDutyResponse[] }) {
  return (
    <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-3 border-b border-purple-200 bg-purple-50 px-5 py-3 text-purple-900">
        <div>
          <span className="font-black">On Official Duty</span>
        </div>
        <span className="rounded-full bg-purple-200 px-2.5 py-0.5 text-xs font-bold text-purple-800">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-500">No employees on official duty today.</p>
      ) : (
        <div className="divide-y">
          {items.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
              <div className="flex min-w-48 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple-100 text-sm font-black text-purple-700">
                  {(item.employeeName ?? "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-black text-slate-900">{item.employeeName ?? "—"}</div>
                  {item.departmentName && (
                    <div className="text-xs text-slate-400">{item.departmentName}</div>
                  )}
                </div>
              </div>
              {item.purpose && (
                <div className="min-w-36">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Purpose</div>
                  <div className="mt-0.5 font-semibold text-slate-700">{item.purpose}</div>
                </div>
              )}
              {item.location && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Location</div>
                  <div className="mt-0.5 text-sm text-slate-700">{item.location}</div>
                </div>
              )}
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Period</div>
                <div className="mt-0.5 font-semibold text-slate-700">
                  {formatDate(item.startDate)} → {formatDate(item.endDate)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
}
